import React, {
  useState,
  useRef,
  useEffect,
  memo,
  useMemo,
  useCallback
} from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import styled from 'styled-components'
import PeopleIcon from '@/components/icons/PeopleIcon'
import PersonIcon from '@/components/icons/PersonIcon'
import VariantSelect from '@/components/shardLibrary/VariantSelect'
import useShardLibrary from '@/hooks/useShardLibrary'
import ReactTooltip from "react-tooltip"
import { SHARD_WIDTH } from '@/config/settings'

const Tooltip = (props: any) => <ReactTooltip
  {...props}
  overridePosition={(position: any, _currentEvent: any, _currentTarget: any, refNode: any) => {
    refNode.style.width = `${SHARD_WIDTH}px`

    return position
  }}
/>

export const colorTypes = [
  {
    name: 'premium',
    fontSize: '10px',
    textTransform: 'uppercase',
    color: '#FF8B48',
    borderColor: '#FF8B48',
    backgroundColor: 'rgba(84, 57, 47, 0.8)',
    filter: 'drop-shadow(0px 4px 20px rgba(255, 139, 72, 0.25))',
    icon: undefined,
  },
  {
    name: 'community',
    fontSize: '10px',
    textTransform: 'uppercase',
    color: '#ADADAD',
    borderColor: '#ADADAD',
    backgroundColor: 'rgba(135, 135, 135, 0.25)',
    boxShadow: '0px 5px 25px rgba(173, 173, 173, 0.25)',
    icon: undefined,
  },
  {
    name: 'public',
    fontSize: '12px',
    textTransform: 'capitalize',
    color: '#9BA3B0',
    borderColor: '#383E50',
    backgroundColor: 'rgba(84, 57, 47, 0.0)',
    boxShadow: '0px 5px 25px rgba(27, 30, 39, 0.5)',
    icon: (
      <PeopleIcon
        style={{ width: 16, height: 15, color: "#9BA3B0", marginRight: 6, marginLeft: -4, marginTop: -2 }}
      />
    ),
  },
  {
    name: 'private',
    fontSize: '12px',
    textTransform: 'capitalize',
    color: '#9BA3B0',
    borderColor: '#383E50',
    backgroundColor: 'rgba(84, 57, 47, 0.0)',
    boxShadow: '0px 5px 25px rgba(27, 30, 39, 0.5)',
    icon: (
      <PersonIcon
        style={{ width: 16, height: 15, color: "#9BA3B0", marginRight: 6, marginLeft: -4, marginTop: -2 }}
      />
    ),
  },
]

const NameOuterContainer = styled.span<{ condensed?: boolean }>`
  justify-content: flex-start;
  width: 184px;
  height: ${props => props.condensed ? '56px' : '44px'};
  display: flex;
  align-items: center;
  min-height: 44px;
  line-height: 44px;
  white-space: nowrap;
`

const NameSpanContainer = styled.span`
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  text-align: left;
  font-weight: 700;
  max-width: 184px;
`

const NameAndTooltipContainer = styled.div`
  position: relative;
  z-index: 0;
`

interface NameProps {
  name?: string
  includeToolTip?: boolean
  nameRef?: React.Ref<HTMLSpanElement>
  condensed?: boolean
  id?: string | number
  redirectToShard?: () => void
}

const Name = ({
  name,
  includeToolTip,
  nameRef,
  condensed,
  id: _id,
  redirectToShard
}: NameProps) => {
  return (
    <NameAndTooltipContainer {...(includeToolTip ? {
      'data-tip': name,
      'data-for': "ShardCardTooltip"
    } : {})}>
      <NameOuterContainer condensed={condensed} onClick={redirectToShard}>
        <NameSpanContainer ref={nameRef}>{name || ''}</NameSpanContainer>
      </NameOuterContainer>
    </NameAndTooltipContainer>
  )
}

const Container = styled.div<{ opacity?: number }>`
  position: relative;
  width: ${SHARD_WIDTH}px;
  height: 282px;
  opacity: ${props => props.opacity};
`

const Card = styled.div<{
  condensed?: boolean
  zIndex?: number
  opacity?: number
  borderColor?: string
  top?: string
  left?: string
  transition?: string
  boxShadow?: string
  filter?: string
}>`
  border-radius: 10px;
  position: absolute;
  width: ${SHARD_WIDTH}px;
  height: ${props => props.condensed ? '103px' : '241px'};
  z-index: ${props => props.zIndex};
  opacity: ${props => props.opacity};
  border: 1px solid ${props => props.borderColor};
  top: ${props => props.top};
  left: ${props => props.left};
  transition: ${props => props.transition};
  background: #1B1E27;
  box-shadow: ${props => props.boxShadow};
  filter: ${props => props.filter};
  flex-direction: column;
  display: flex;
  cursor: pointer;
`

const ScreenShotContainer = styled.div`
  height: 148px;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`

const ScreenShot = styled.div<{ screenshotThumbnail?: string }>`
  max-width: 143px;
  width: 143px;
  height: 111px;
  max-height: 111px;
  position: absolute;
  z-index: 3;
  background-image: url('${props => props.screenshotThumbnail}');
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  opacity: 1;
`

const PlaceHolderContainer = styled.div`
  width: 100%;
  position: absolute;
  z-index: 4;
  top: 0px;
  left: 0px;
  height: 148px;
  background-image: url('./logo_mark_bordered.png');
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  opacity: .05;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`

const PlaceHolderText = styled.div`
  width:100%;
  position: absolute;
  z-index: 5;
  top: 0px;
  left: 0px;
  height: 148px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 19.49px;
  letter-spacing: 5.5%;
  font-family: proxima nova;
  font-weight: 700;
  background: radial-gradient(50% 50% at 50% 50%, #222632 0%, #141721 100%);;
  color: rgba(255, 255, 255, 0.33);
  opacity: 0.5;
`

const NameAndInfoContainer = styled.div<{ condensed?: boolean }>`
  width: 100%;
  padding: 0px 15px 0px 15px;
  background: #212734;
  height: ${props => props.condensed ? '100%' : '93px'};
  border-top-left-radius: ${props => props.condensed ? '10px' : '0px'};
  border-top-right-radius: ${props => props.condensed ? '10px' : '0px'};
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  position: relative;
`

const VisibilityResolutionAndDevice = styled.div<{ variants?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-top: ${props => props.variants ? '0px solid #3E455A' : '1px solid #3E455A' };
  height: 45px;
`

const VisibilityTagContainer = styled.div<{
  fontSize?: string
  textTransform?: string
  color?: string
  backgroundColor?: string
}>`
  min-width: 61px;
  height: 18px;
  font-size: ${props => props.fontSize};
  padding: 0px 5px 0px 5px;
  border-radius: 3px;
  text-transform: ${props => props.textTransform as any};
  font-weight: 700;
  text-align: center;
  line-height: 18.5px;
  color: ${props => props.color};
  background-color: ${props => props.backgroundColor};
`

const ResolutionAndDevices = styled.div`
  display: flex;
  flex-direction: row;
  color:#9BA3B0;
  font-size: 12px;
  font-weight: 700;
  align-items: center;
`

const Resolution = styled.div`
  height: 15px;
  margin-top: -3px;
`

interface ShardCardProps {
  isLegacy?: boolean
  image?: string
  url?: string
  name?: string
  screenshot_thumbnail?: string
  width?: number
  height?: number
  id: number
  variants?: any[]
  type?: string
  condensed?: boolean
  source_shard?: { id?: number }
  editing_allowed?: boolean
  [key: string]: any
}

const ShardCard = ({
  isLegacy,
  image,
  url,
  name,
  screenshot_thumbnail,
  width,
  height,
  id,
  variants = [],
  type,
  condensed = false,
  source_shard,
  ...rest
}: ShardCardProps) => {
  const colorsAndName = colorTypes.find((typeRow) => typeRow.name === type)
  const [cardHover, setCardHover] = useState(false)
  const [includeToolTip, setIncludeToolTip] = useState(false)
  const [variantDropdownZIndex, setVariantDropdownZIndex] = useState(2)
  const { selectedVariantShards, setVariantShard } = useShardLibrary()

  const navigate = useNavigate()
  const { collectionId } = useParams({ strict: false })

  const nameRef = useRef<HTMLSpanElement>(null)

  const isOverflowActive = (ref: React.RefObject<HTMLSpanElement | null>) => {
    const current = ref.current
    if (current) {
      return current.offsetWidth < current.scrollWidth
    }
    return false
  }

  useEffect(() => {
    if (isOverflowActive(nameRef)) {
      setIncludeToolTip(true)
    }
  }, [nameRef, setIncludeToolTip, name])

  const selectedVariant = useMemo(() => {
    if (selectedVariantShards && variants.length > 0) {
      return selectedVariantShards.get(id) || id
    }
    return id
  }, [id, selectedVariantShards, variants.length])

  const redirectToShard = useCallback((variantClickable: boolean) => {
    if (!rest.editing_allowed) return

    if ((variantClickable && variants.length > 0) || (!(variants.length > 0) && !variantClickable)) {
      if (url) {
        navigate({ to: url })
      } else {
        let shardUrl = `/shardlibrary/${collectionId}/${selectedVariant}`

        if (source_shard?.id) {
          shardUrl += `?sourceShardId=${source_shard?.id}`
        }

        navigate({ to: shardUrl })
      }
    }
  }, [selectedVariant, navigate, collectionId, variants, url, source_shard])

  const variantResolutions = useMemo(() => {
    if (variants) {
      const variantResolutions = variants.reduce((accumulator: any[], currentValue: any) => {
        accumulator.push({ value: currentValue.id, label: currentValue.width && currentValue.height ? `${currentValue.width}x${currentValue.height}` : `No resolution set variant ${currentValue.id}` })
        return accumulator
      }, [])
      variantResolutions.unshift({ value: id, label: width && height ? `${width}x${height}` : `No resolution set on parent` })
      return variantResolutions
    }
    return []
  }, [width, height, variants])

  return (
    <>
      <Container
        onClick={() => redirectToShard(false)}
        opacity={+!!rest.editing_allowed + 0.5}
        {...(rest.editing_allowed ? {} : {
          'data-tip': 'All free shard slots used.<br/>Upgrade to GlassPass to remove this limit.',
          'data-for': "ShardCardTooltip",
          'data-multiline': true
        })}
      >
        {!!variants.length && (
          <>
            {!condensed && <Card zIndex={0} opacity={0.15} top={cardHover ? '10px' : '20px'} left="20px" borderColor={colorsAndName?.borderColor} transition="all 0.6s ease-in-out" />}
            <Card zIndex={1} opacity={0.35} top={cardHover ? '0px' : '10px'} left="10px" borderColor={colorsAndName?.borderColor} transition="all 0.30s ease-in-out" condensed={condensed} />
          </>
        )}
        <Card
          zIndex={variantDropdownZIndex}
          onMouseEnter={() => setCardHover(true)}
          onMouseLeave={() => setCardHover(false)}
          opacity={1}
          top={cardHover ? '-10px' : '0px'}
          left="0px"
          borderColor={colorsAndName?.borderColor}
          transition="all 0.15s ease-in-out"
          boxShadow={cardHover ? colorsAndName?.boxShadow || '' : ''}
          filter={cardHover ? colorsAndName?.filter || '' : ''}
          condensed={condensed}
        >
          {isLegacy ? (
            <div

              // @ts-ignore
              css={`
            width: 100%;
            height: 100%;
            border-radius: inherit;
            background-image: url("${image}");
            background-position: center;
            background-size: cover;
          `}
            />
          ) : (
            <>
              {!condensed && (
                <ScreenShotContainer onClick={() => redirectToShard(true)}>
                  {screenshot_thumbnail ? (
                    <>
                      <ScreenShot screenshotThumbnail={screenshot_thumbnail} />
                    </>
                  ) : (
                    <>
                      <PlaceHolderContainer />
                      <PlaceHolderText>
                  Placeholder
                      </PlaceHolderText>
                    </>
                  )}
                </ScreenShotContainer>
              )}
              <NameAndInfoContainer condensed={condensed}>
                <Name
                  includeToolTip={includeToolTip}
                  name={name}
                  nameRef={nameRef}
                  condensed={condensed}
                  id={id}
                  redirectToShard={() => redirectToShard(true)}
                />
                <VisibilityResolutionAndDevice variants={variants.length > 0}>
                  {variants.length > 0 ? (
                    <VariantSelect variantResolutions={variantResolutions} selectedVariant={selectedVariant} setVariantShard={setVariantShard} parentShardId={id} setVariantDropdownZIndex={setVariantDropdownZIndex} />
                  ) : (
                    <>
                      <VisibilityTagContainer fontSize={colorsAndName?.fontSize} textTransform={colorsAndName?.textTransform} color={colorsAndName?.color} backgroundColor={colorsAndName?.backgroundColor}>
                        {colorsAndName?.icon && colorsAndName?.icon}
                        {colorsAndName?.name}
                      </VisibilityTagContainer>
                      <ResolutionAndDevices>
                        <Resolution>
                          {variants?.length > 0
                            ? ''
                            : width && height
                              ? `${width}x${height}`
                              : ''}
                        </Resolution>
                      </ResolutionAndDevices>
                    </>
                  )}
                </VisibilityResolutionAndDevice>
              </NameAndInfoContainer>
            </>
          )}
        </Card>
      </Container>
      <Tooltip id='ShardCardTooltip' />
    </>
  )
}

export default memo(ShardCard)
